import config from 'config';

import { isReachable } from './is-reachable';
import Keycloak from './keycloak';
import { isSlackEmbedPathname } from './slack-embed-location';

type Merge<M, N> = Omit<M, Extract<keyof M, keyof N>> & N;

type NeptuneKeycloak = Merge<
  Keycloak.KeycloakInstance,
  {
    tokenParsed?: Merge<
      Keycloak.KeycloakTokenParsed,
      {
        allow_new_ui?: string;
        loginMethod?: string;
        preferred_username?: string;
        neptune_role?: string;
        neptune_instance_role?: string;
      }
    >;
  }
>;

const SLACK_EMBED_DEFAULT_CLIENT_ID = 'slack-embed';

// The singleton is created before any bootstrap code runs, so the Slack embed client is chosen here.
const keycloak: NeptuneKeycloak = Keycloak({
  url: config.keycloakURL,
  realm: config.keycloakRealm,
  clientId: getKeycloakClientId(window.location.pathname),
}) as NeptuneKeycloak;

export default keycloak;
export const authClient = keycloak;

export const REFRESH_TOKEN_MIN_VALIDITY = 30;

export async function isAuthServerReachable(): Promise<boolean> {
  // use realm endpoint because it is available cross domain
  const url = getRealmUrl();
  return isReachable(url);
}

function getRealmUrl(): string {
  if (!keycloak.authServerUrl || !keycloak.realm) {
    return '';
  }

  const pathSuffix = keycloak.authServerUrl.endsWith('/') ? '' : '/';
  return `${keycloak.authServerUrl}${pathSuffix}realms/${encodeURIComponent(keycloak.realm)}`;
}

export function getKeycloakInitOptions(): Keycloak.KeycloakInitOptions {
  return {
    enableLogging: config.enableKeycloakLogging,
    onLoad: 'check-sso',
    silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso/index.html`,
  };
}

export function getKeycloakClientId(pathname: string): string {
  if (isSlackEmbedPathname(pathname)) {
    return config.keycloakEmbedClientId || SLACK_EMBED_DEFAULT_CLIENT_ID;
  }

  return config.keycloakFrontendClientId;
}

export function getSlackEmbedKeycloakInitOptions(tokens: {
  accessToken: string;
  refreshToken: string;
  idToken?: string;
}): Keycloak.KeycloakInitOptions {
  return {
    token: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    idToken: tokens.idToken,
    checkLoginIframe: false,
    enableLogging: config.enableKeycloakLogging,
    pkceMethod: 'S256',
  };
}

export function getPreferredUsername(): string {
  if (keycloak.tokenParsed?.loginMethod === undefined) {
    return '';
  }

  return keycloak.tokenParsed?.preferred_username || '';
}
